import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Container, Sprite, Text } from '@pixi/react';
import gsap from 'gsap';

import { getGameImages } from '../../../core/helpers/getGameImages';
import { useOutSideClick } from '../../../core/hooks/useOutSideClick';
import { textureChangeFrame } from '../../../core/helpers/textureChangeFrame';

import { bettingPartStore } from '../../../store/bettingPartStore';

import { gameService } from '../../../services/gameService';
import { isMobile } from '../../../core/helpers/isMobile';

let popupListTimeLine = null;

const AutoBetListPopup = observer(({ x = 0, y = 0 }) => {
  const [hoverAutoBetCount, setHoverAutoBetCount] = useState(null);
  const {
    isOpenAutoBetPopup,
    selectedAutoBetCount,
    setIsAutoBetStart,
    setIsOpenAutoBetPopup,
    setSelectedAutoBetCount,
  } = bettingPartStore;
  const [autoBetListRef, isOutsideClick, setIsOutsideClick] = useOutSideClick();

  const {
    autoBetTopCount,
    autoBetTopCountPressed,
    autoBetTopCountSelected,
    autoBetCount,
    autoBetCountPressed,
    autoBetCountSelected,
    autoBetBottomCount,
    autoBetBottomCountPressed,
    autoBetBottomCountSelected,
    infinityIcon,
  } = useMemo(() => {
    return getGameImages();
  }, []);

  useEffect(() => {
    if (!isOpenAutoBetPopup) return;
    popupListTimeLine = gsap.to(autoBetListRef.current.scale, {
      x: 1,
      y: 1,
      duration: 0.1,
    });
    /* eslint-disable-next-line */
    return () => {
      setIsOutsideClick({ hasClickedOutside: false });
      popupListTimeLine?.kill();
      popupListTimeLine = null;
    };
  }, [isOpenAutoBetPopup, autoBetListRef, setIsOutsideClick]);

  useEffect(() => {
    if (isOutsideClick && isOpenAutoBetPopup) {
      setIsOpenAutoBetPopup(false);
    }
  }, [isOutsideClick, isOpenAutoBetPopup, setIsOpenAutoBetPopup]);

  const autoBetList = useMemo(() => {
    return {
      10: {
        value: 10,
        image: autoBetTopCount,
        text: '10',
        isSprite: false,
        hoverImg: autoBetTopCountPressed,
        pressedImg: autoBetTopCountPressed,
        selectedImg: autoBetTopCountSelected,
      },
      20: {
        value: 20,
        image: autoBetCount,
        text: '20',
        isSprite: false,
        hoverImg: autoBetCountPressed,
        pressedImg: autoBetCountPressed,
        selectedImg: autoBetCountSelected,
      },
      50: {
        value: 50,
        image: autoBetCount,
        text: '50',
        isSprite: false,
        hoverImg: autoBetCountPressed,
        pressedImg: autoBetCountPressed,
        selectedImg: autoBetCountSelected,
      },
      100: {
        value: 100,
        image: autoBetCount,
        text: '100',
        isSprite: false,
        hoverImg: autoBetCountPressed,
        pressedImg: autoBetCountPressed,
        selectedImg: autoBetCountSelected,
      },
      Infinity: {
        value: Infinity,
        image: autoBetBottomCount,
        text: 'Infinity',
        isSprite: true,
        hoverImg: autoBetBottomCountPressed,
        pressedImg: autoBetBottomCountPressed,
        selectedImg: autoBetBottomCountSelected,
      },
    };
  }, [
    autoBetCount,
    autoBetTopCount,
    autoBetBottomCount,
    autoBetCountPressed,
    autoBetCountSelected,
    autoBetTopCountPressed,
    autoBetTopCountSelected,
    autoBetBottomCountPressed,
    autoBetBottomCountSelected,
  ]);

  const detectImage = useCallback(
    (value) => {
      const autoBetModel = autoBetList[value];
      const autoBetCountImg = autoBetList[value].image;
      if (selectedAutoBetCount === value) {
        return autoBetModel.selectedImg;
      }
      if (hoverAutoBetCount === value) {
        return autoBetModel.pressedImg;
      }
      return autoBetCountImg;
    },
    [selectedAutoBetCount, hoverAutoBetCount, autoBetList]
  );

  const handlePointerUpOutSide = useCallback(
    (e) => {
      e.stopPropagation();
      setIsOpenAutoBetPopup(false);
    },
    [setIsOpenAutoBetPopup]
  );

  const handlePointerUp = useCallback(
    (e) => {
      e.stopPropagation();
      const { value } = e.target;
      setSelectedAutoBetCount(value);
      setIsAutoBetStart(true);
      setHoverAutoBetCount(null);
      gameService.doSpin();
    },
    [setIsAutoBetStart, setSelectedAutoBetCount]
  );

  const handlePointerDown = useCallback(
    (e) => {
      e.stopPropagation();
      const { value } = e.target;
      textureChangeFrame(e.target.children[0], autoBetList[value].pressedImg);
    },
    [autoBetList]
  );

  if (!isOpenAutoBetPopup) {
    return null;
  }

  return (
    <Container y={y} x={x} ref={autoBetListRef} scale={{ x: 0, y: 0 }} pivot={{ y: 270 }}>
      {Object.values(autoBetList).map(({ text, value, isSprite }, index) => {
        return (
          <Container
            key={text}
            y={index * 54}
            value={value}
            interactive
            cursor='pointer'
            pointerupoutside={handlePointerUpOutSide}
            pointerup={handlePointerUp}
            pointerdown={handlePointerDown}
          >
            <Sprite texture={detectImage(value)} anchor={0.5} scale={isMobile() ? 0.77 : 0.5} />
            {isSprite ? (
              <Sprite texture={infinityIcon} anchor={0.5} scale={isMobile() ? 0.77 : 0.5} />
            ) : (
              <Text
                text={text}
                style={{ _fill: '#FFFFFF', _fontWeight: 500, _fontSize: 20 }}
                anchor={0.5}
                resolution={2}
              />
            )}
          </Container>
        );
      })}
    </Container>
  );
});

export default AutoBetListPopup;
